import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { AuthLayout } from "@/pages/Login";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

export default function Signup() {
  const { t, language } = useLanguage();
  const { signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await signup({ name, email, password, language });
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <AuthLayout>
      <div className="rounded-2xl border border-slate-200/80 bg-white p-7 shadow-premium sm:p-8" data-testid="signup-card">
        <h1 className="font-display text-2xl font-bold text-slate-900">{t("signup_title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("signup_subtitle")}</p>

        <Button type="button" variant="outline" onClick={loginWithGoogle} data-testid="google-signup-btn" className="mt-6 min-h-[48px] w-full gap-2.5 rounded-xl">
          <GoogleIcon /> {t("continue_google")}
        </Button>

        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs uppercase text-slate-400">{t("or")}</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="name">{t("name")}</Label>
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name-input" className="mt-1.5 min-h-[48px]" autoComplete="name" />
          </div>
          <div>
            <Label htmlFor="email">{t("email")}</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email-input" className="mt-1.5 min-h-[48px]" autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="password">{t("password")}</Label>
            <div className="relative mt-1.5">
              <Input id="password" type={show ? "text" : "password"} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password-input" className="min-h-[48px] pr-12" autoComplete="new-password" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={show ? t("hide") : t("show")}>
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-400">Minimum 8 characters.</p>
          </div>
          {error && <p className="text-sm text-rose-600" data-testid="signup-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="signup-submit-btn" className="min-h-[48px] w-full rounded-xl font-semibold">
            {loading ? t("loading") : t("signup")}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          {t("have_account")}{" "}
          <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="go-login-link">{t("login")}</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
